import React, { memo, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import {
	Button,
	FormControlLabel,
	GenericSwitch,
	HookSelectFormGroup,
	Skeleton,
} from "components";
import { AchFeeTemplateFields } from "globalConstants";
import { useLoader } from "hooks";
import { useCheckAchFeeTemplateNameMutation } from "queries";
import { FeeTemplatesStatusTypes } from "types";

import FeeTemplateField from "../common/feeTemplateField";
import FeeTemplateNameField from "../common/feeTemplateNameField";

import styles from "./achFeeTemplateForm.module.scss";

type AchFeeTemplateFormProps = {
	companyId: string;
	defaultValues?: { [key: string]: any };
	isLoading?: boolean;
	linkToListing: string;
	isSingle: boolean;
	isEdit?: boolean;
	onSubmit: Function;
};

const {
	IsDefault,
	Status,
	TemplateName,
	SetupFee,
	MonthlyFee,
	TransactionFee,
	OriginationDiscountFee,
	OriginationLargeTransactionDiscountFee,
	StandardReturnFee,
	NoticeOfChangeReturnFee,
	ChargebackReturnFee,
	AccountVerificationFee,
	AccountVerificationPlusFee,
} = AchFeeTemplateFields;

const statusOptions = FeeTemplatesStatusTypes.getOptions();

const AchFeeTemplateForm = ({
	companyId,
	defaultValues = {},
	isLoading,
	linkToListing,
	isSingle,
	isEdit,
	onSubmit,
}: AchFeeTemplateFormProps) => {
	const history = useHistory();

	const Loader = useLoader(isLoading);

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		values: defaultValues,
	});

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		formState: { isSubmitting },
	} = formMethods;

	const handleStatusChange = useCallback(
		fieldValue => {
			if (
				fieldValue.value === FeeTemplatesStatusTypes.Unavailable.value
			) {
				setValue(IsDefault.name, false);
			}
		},
		[setValue],
	);

	const handleSubmitForm = useCallback(
		formValues => {
			onSubmit(
				isEdit
					? formValues
					: {
							...formValues,
							[Status.name]: FeeTemplatesStatusTypes.Available,
					  },
			);
		},
		[isEdit, onSubmit],
	);

	const handleCancel = useCallback(() => {
		history.push(linkToListing);
	}, [history, linkToListing]);

	const isAvailable = !!watch(Status.name)?.value;

	const isDefaultInitial = defaultValues?.[IsDefault.name];

	const FeeTemplateFieldSetup = useCallback(
		props => <FeeTemplateField isLoading={isLoading} {...props} />,
		[isLoading],
	);

	return (
		<div className={styles.container}>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					<Row className="pb-4">
						<Col>
							<Loader
								skeleton={<Skeleton height={34} />}
								component={
									<FormControlLabel
										control={
											<GenericSwitch
												register={register}
												name={IsDefault.name}
												id={IsDefault.name}
												isDisabled={
													isSingle ||
													!isAvailable ||
													isDefaultInitial
												}
											/>
										}
										label={IsDefault.displayName}
									/>
								}
							/>
						</Col>
					</Row>
					<Row className="pb-4">
						<Col lg={5}>
							{isEdit && (
								<HookSelectFormGroup
									name={Status.name}
									label={Status.displayName}
									placeholder="Please Select"
									options={statusOptions}
									getOptionLabel={option =>
										option.displayName
									}
									getOptionValue={option => option.value}
									isSearchable={false}
									defaultOptions
									blurLoading={isLoading}
									isDisabled={isSingle || isDefaultInitial}
									onChange={handleStatusChange}
								/>
							)}
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateNameField
								isLoading={isLoading}
								companyId={companyId}
								fieldInitialValue={
									defaultValues?.[TemplateName.name]
								}
								fieldData={TemplateName}
								useCheckFeeTemplateNameMutation={
									useCheckAchFeeTemplateNameMutation
								}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup data={SetupFee} />
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup data={MonthlyFee} />
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup data={TransactionFee} />
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={OriginationDiscountFee}
								isPercent
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={OriginationLargeTransactionDiscountFee}
								isPercent
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup data={StandardReturnFee} />
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={NoticeOfChangeReturnFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup data={ChargebackReturnFee} />
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={AccountVerificationFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={AccountVerificationPlusFee}
							/>
						</Col>
					</Row>

					<Row className={styles.buttonWrapper}>
						<Col>
							<Button
								onClick={handleCancel}
								value="Cancel"
								variant="outline-primary"
							/>
							<Button
								disabled={isSubmitting || isLoading}
								value="Save"
								variant="primary"
								type="submit"
							/>
						</Col>
					</Row>
				</form>
			</FormProvider>
		</div>
	);
};

export default memo(AchFeeTemplateForm);
